/**
 * AIGO Shipping Selector
 *
 * Responsibilities:
 *  - Province cascade for #co-country/#co-state (desktop) and #mco-country/#mco-state (mobile)
 *    — only for user interaction on the new-address form; initial state is set by the
 *    address-picker IIFE in aigo-main-cart.liquid via syncFormFromCard.
 *  - Fetch shipping rates from /cart/shipping_rates.json (when address is entered)
 *  - Update order summary sidebar (shows Free ✓ when free_delivery_for_all is enabled)
 *  - Expose window.AigoShipping for draft-order.js
 */
(function () {
  'use strict';

  let rates = [];
  let selectedIdx = 0;
  let fetchTimer = null;

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    const card = document.getElementById('aigo-ship-card');
    if (!card) return;

    // Register cascade listeners for user interaction on the new-address form.
    // Does NOT pre-select country/province — the address-picker IIFE handles
    // initial state via syncFormFromCard.
    setupCascadeListeners(card, 'co-country',  'co-state');
    setupCascadeListeners(card, 'mco-country', 'mco-state');

    // Debounced triggers on address fields (fires when user types in new-address form)
    ['co-zip', 'co-city', 'mco-zip', 'mco-city'].forEach(function(id) {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', function() {
        clearTimeout(fetchTimer);
        fetchTimer = setTimeout(function() { fetchRates(card); }, 500);
      });
    });

  }

  // ── Province cascade (user interaction only) ──────────────────────────────────

  /**
   * Register change listeners on country/province selects.
   * When the user changes the country in the new-address form, provinces are
   * rebuilt automatically. No initial state is set here — that's the cart IIFE's job.
   */
  function setupCascadeListeners(card, countryId, stateId) {
    const countrySel  = document.getElementById(countryId);
    const provinceSel = document.getElementById(stateId);
    if (!countrySel || !provinceSel) return;

    countrySel.addEventListener('change', function() {
      updateProvinces(countrySel, provinceSel, '');
      clearTimeout(fetchTimer);
      fetchTimer = setTimeout(function() { fetchRates(card); }, 200);
    });
    provinceSel.addEventListener('change', function() {
      clearTimeout(fetchTimer);
      fetchTimer = setTimeout(function() { fetchRates(card); }, 200);
    });
  }

  function updateProvinces(countrySel, provinceSel, defaultProvince) {
    const selected = countrySel.options[countrySel.selectedIndex];
    let provinces = [];
    try { provinces = JSON.parse(selected && selected.dataset.provinces || '[]'); } catch (e) { /* empty */ }

    provinceSel.innerHTML = '';
    if (provinces.length === 0) {
      provinceSel.closest('.fld, .mco-fld') && (provinceSel.closest('.fld, .mco-fld').style.display = 'none');
      return;
    }

    const wrap = provinceSel.closest('.fld, .mco-fld');
    if (wrap) wrap.style.display = '';

    provinces.forEach(function(pair) {
      const opt = document.createElement('option');
      opt.value = pair[0]; // province code
      opt.textContent = pair[1];
      opt.selected = (pair[0] === defaultProvince || pair[1] === defaultProvince);
      provinceSel.appendChild(opt);
    });
  }

  // ── Address helpers ───────────────────────────────────────────────────────────

  /** Returns full address from whichever form (desktop or mobile) is active. */
  function getAddress() {
    // prefer whichever country select is in a visible viewport
    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    const prefix   = isMobile ? 'mco' : 'co';

    function val(id) {
      const el = document.getElementById(id);
      return el ? el.value.trim() : '';
    }

    return {
      first_name: val(prefix + '-first'),
      last_name:  val(prefix + '-last'),
      address1:   val(prefix + '-addr1'),
      address2:   val(prefix + '-addr2'),
      city:       val(prefix + '-city'),
      country:    val(prefix + '-country'),
      province:   val(prefix + '-state'),
      zip:        val(prefix + '-zip'),
    };
  }

  // ── Shipping rate fetching ────────────────────────────────────────────────────

  function showShipEmpty(show) {
    const emptyEl = document.getElementById('co-ship-empty');
    const resEl   = document.getElementById('ship-results');
    if (emptyEl) emptyEl.style.display = show ? '' : 'none';
    if (resEl && show) resEl.innerHTML = '';
  }

  /**
   * Fetch shipping rates.
   * @param {Element} card — #aigo-ship-card element (for reading membership data)
   */
  async function fetchRates(card) {
    const addr = getAddress();
    if (!addr.zip) {
      showShipEmpty(true);
      return;
    }

    showShipEmpty(false);

    const desktopRes = document.getElementById('ship-results');
    const mobileRes  = document.getElementById('mco-ship-results');

    setLoadingRates(desktopRes, mobileRes);

    try {
      // Delegate to Cart module so its _rates cache stays in sync with ours.
      // Fall back to direct fetch if Cart module is not available.
      if (window.FoxTheme && window.FoxTheme.Cart) {
        rates = await window.FoxTheme.Cart.fetchShippingRates(addr);
        selectedIdx = 0;
        if (rates.length === 0) {
          setRateError(desktopRes, mobileRes, "Sorry, we don\u2019t ship to that address.");
        } else {
          renderRates(desktopRes, mobileRes);
        }
      } else {
        const res = await fetch('/cart/shipping_rates.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ shipping_address: { zip: addr.zip, country: addr.country, province: addr.province } }),
        });
        const data = await res.json();

        if (Array.isArray(data.shipping_rates)) {
          if (data.shipping_rates.length === 0) {
            setRateError(desktopRes, mobileRes, "Sorry, we don\u2019t ship to that address.");
            rates = [];
          } else {
            rates = data.shipping_rates.slice().sort(function(a, b) {
              return parseFloat(a.price) - parseFloat(b.price);
            });
            selectedIdx = 0;
            renderRates(desktopRes, mobileRes);
          }
        } else {
          const msgs = Object.values(data).flat().filter(function(v) { return typeof v === 'string' && v; }).join(' ');
          setRateError(desktopRes, mobileRes, msgs || 'Could not retrieve rates — check your address.');
          rates = [];
        }
      }
    } catch (err) {
      console.error('[AigoShipping] fetchRates error', err);
      setRateError(desktopRes, mobileRes, 'Network error. Please try again.');
      rates = [];
    }

    updateSummary(card);
  }

  function setLoadingRates(desktopEl, mobileEl) {
    const html = '<p style="color:var(--fg-muted);font-size:13px;padding:8px 0">Calculating rates\u2026</p>';
    if (desktopEl) desktopEl.innerHTML = html;
    if (mobileEl)  mobileEl.innerHTML  = html;
  }

  function setRateError(desktopEl, mobileEl, msg) {
    const html = '<p class="ship-error" style="font-size:13px;color:var(--fg-muted);padding:8px 0">' + msg + '</p>';
    if (desktopEl) desktopEl.innerHTML = html;
    if (mobileEl)  mobileEl.innerHTML  = html;
  }

  // ── Rate rendering ────────────────────────────────────────────────────────────

  function renderRates(desktopEl, mobileEl) {
    if (desktopEl) renderDesktopRates(desktopEl);
    if (mobileEl)  renderMobileRates(mobileEl);
  }

  function renderDesktopRates(container) {
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'ship-rates';

    rates.forEach(function(rate, i) {
      const label = document.createElement('label');
      label.className = 'ship-rate' + (i === 0 ? ' ship-rate--selected' : '');

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'ship-option';
      radio.value = String(i);
      radio.checked = i === 0;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'ship-rate__name';
      const nameB = document.createElement('b');
      nameB.textContent = rate.presentment_name;
      nameSpan.appendChild(nameB);

      const priceSpan = document.createElement('span');
      priceSpan.className = 'ship-rate__price';
      priceSpan.textContent = rate.currency + ' ' + parseFloat(rate.price).toFixed(2);

      label.appendChild(radio);
      label.appendChild(nameSpan);
      label.appendChild(priceSpan);
      wrap.appendChild(label);

      radio.addEventListener('change', function() {
        selectedIdx = i;
        if (window.FoxTheme && window.FoxTheme.Cart) window.FoxTheme.Cart.setSelectedRate(i);
        wrap.querySelectorAll('.ship-rate').forEach(function(el, j) {
          el.classList.toggle('ship-rate--selected', j === i);
        });
        updateSummary(document.getElementById('aigo-ship-card'));
      });
    });

    container.appendChild(wrap);
  }

  function renderMobileRates(container) {
    container.innerHTML = '';

    rates.forEach(function(rate, i) {
      const label = document.createElement('label');
      label.className = 'mco-opt' + (i === 0 ? ' mco-opt--on' : '');

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'mship';
      radio.checked = i === 0;

      const main = document.createElement('span');
      main.className = 'mco-opt__main';
      main.innerHTML = '<div class="mco-opt__name">' + escHtml(rate.presentment_name) + '</div>';

      const price = document.createElement('span');
      price.className = 'mco-opt__price';
      price.textContent = rate.currency + ' ' + parseFloat(rate.price).toFixed(2);

      label.appendChild(radio);
      label.appendChild(main);
      label.appendChild(price);
      container.appendChild(label);

      radio.addEventListener('change', function() {
        selectedIdx = i;
        if (window.FoxTheme && window.FoxTheme.Cart) window.FoxTheme.Cart.setSelectedRate(i);
        container.querySelectorAll('.mco-opt').forEach(function(el, j) {
          el.classList.toggle('mco-opt--on', j === i);
        });
        updateSummary(document.getElementById('aigo-ship-card'));
      });
    });
  }

  // ── Summary update ─────────────────────────────────────────────────────────────

  function updateSummary(card) {
    if (!card) return;
    const freeShipping      = card.dataset.freeShipping     === 'true';
    const estSubtotalCents  = parseInt(card.dataset.estSubtotalCents || '0', 10);

    const shippingVal      = document.getElementById('sum-shipping-val');
    const shipBanner       = document.getElementById('sum-ship-banner');
    const shipLabel        = document.getElementById('sum-ship-label');
    const shipDiscountRow  = document.getElementById('sum-ship-discount-row');
    const shipDiscountVal  = document.getElementById('sum-ship-discount-val');
    const totalVal         = document.getElementById('sum-total-val');

    // Mobile counterparts
    const mobShippingVal   = document.getElementById('mco-shipping-val');
    const mobShipBanner    = document.getElementById('mco-ship-banner');
    const mobShipLabel     = document.getElementById('mco-ship-label');
    const mobShipDiscRow   = document.getElementById('mco-ship-discount-row');
    const mobShipDiscVal   = document.getElementById('mco-ship-discount-val');
    const mobTotalVal      = document.getElementById('mco-total-val');
    const mobBarTotal      = document.getElementById('mco-bar-total');

    if (!shippingVal) return;

    if (freeShipping) {
      // Show "Free" — do not expose rate amounts since fees are not yet finalised
      shippingVal.textContent = 'Free';
      if (shipBanner) shipBanner.classList.remove('paid');
      if (shipLabel) shipLabel.textContent = 'Standard Delivery';
      if (mobShippingVal) mobShippingVal.textContent = 'Free';
      if (mobShipBanner) mobShipBanner.classList.remove('paid');
      if (shipDiscountRow) shipDiscountRow.style.display = 'none';
      if (mobShipDiscRow) mobShipDiscRow.style.display = 'none';

      // Total excludes shipping
      if (totalVal) totalVal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      if (mobTotalVal) mobTotalVal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      if (mobBarTotal) mobBarTotal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      return;
    }

    if (rates.length === 0) {
      shippingVal.textContent = '\u2014';
      if (shipBanner) shipBanner.classList.add('paid');
      if (shipDiscountRow) shipDiscountRow.style.display = 'none';
      if (totalVal) totalVal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      if (mobTotalVal) mobTotalVal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      // Mobile parity
      if (mobShippingVal) mobShippingVal.textContent = '\u2014';
      if (mobShipBanner) mobShipBanner.classList.add('paid');
      if (mobShipDiscRow) mobShipDiscRow.style.display = 'none';
      if (mobBarTotal) mobBarTotal.textContent = '~' + fmtMoney(Math.max(0, estSubtotalCents - (window._aigoPromoCents || 0)));
      return;
    }

    const selected      = rates[selectedIdx];
    const selectedCents = Math.round(parseFloat(selected.price) * 100);
    const totalCents    = estSubtotalCents + selectedCents;

    shippingVal.textContent = selected.currency + ' ' + parseFloat(selected.price).toFixed(2);
    if (shipLabel) shipLabel.textContent = selected.presentment_name;
    if (shipBanner) shipBanner.classList.add('paid');
    if (shipDiscountRow) shipDiscountRow.style.display = 'none';
    if (mobShippingVal) mobShippingVal.textContent = selected.currency + ' ' + parseFloat(selected.price).toFixed(2);
    if (mobShipLabel) mobShipLabel.textContent = selected.presentment_name;
    if (mobShipBanner) mobShipBanner.classList.add('paid');
    if (mobShipDiscRow) mobShipDiscRow.style.display = 'none';

    if (totalVal) totalVal.textContent = '~' + fmtMoney(Math.max(0, totalCents - (window._aigoPromoCents || 0)));
    if (mobTotalVal) mobTotalVal.textContent = '~' + fmtMoney(Math.max(0, totalCents - (window._aigoPromoCents || 0)));
    if (mobBarTotal) mobBarTotal.textContent = '~' + fmtMoney(Math.max(0, totalCents - (window._aigoPromoCents || 0)));
  }

  function fmtMoney(cents) {
    const sym = window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol || '$';
    return sym + (cents / 100).toFixed(2);
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Public API for draft-order.js ─────────────────────────────────────────────

  window.AigoShipping = {
    /** Full address from whichever form is active (used by draft-order.js). */
    getAddress: getAddress,

    /** Trigger a rate refresh from outside (e.g. the address-picker IIFE). */
    refreshRates: function() {
      var card = document.getElementById('aigo-ship-card');
      if (card) fetchRates(card);
    },

    /** Returns selected shipping rate {title, price, currency} or null. */
    getSelectedRate: function() {
      if (!rates.length) return null;
      const r = rates[selectedIdx];
      return { title: r.presentment_name, price: r.price, currency: r.currency };
    },

    /** Cancel any pending debounced rate fetch. */
    cancelPendingFetch: function() {
      clearTimeout(fetchTimer);
    },
  };

  document.addEventListener('DOMContentLoaded', init);
})();
